import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";

/**
 * ExportButton
 * ─────────────
 * Dropdown with two options:
 *   1. Export CSV  — builds a CSV from metrics and triggers download
 *   2. Export PDF  — uses window.print() with a print-specific stylesheet
 *
 * Props:
 *   metrics  — merged metrics object from AnalyticsDashboard
 *   currency — string e.g. "AED"
 *   period   — "weekly" | "monthly" | "yearly" | "custom"
 *   dateRange — { start: string, end: string }
 */

const fmt = (v, decimals = 0) =>
    v == null ? "" : Number(v).toFixed(decimals);

// ── CSV builder ───────────────────────────────────────────────────────────────
function buildCSV(metrics, currency, period, dateRange, isClinicService, isSportsService, crewSingular = "Crew", crewPlural = "Crew") {
    const bookingLabel = isClinicService ? "Appointment" : "Booking";
    const customerLabel = isClinicService ? "Patient" : isSportsService ? "Player" : "Customer";
    const rows = [];
    const sep = ",";

    const push = (section, label, current, previous = "") =>
        rows.push([section, label, current, previous]);

    rows.push(["Section", "Metric", `Current (${period})`, "Previous Period"]);
    rows.push([]);

    // Operations
    push("Operations", `Total ${bookingLabel}s`, fmt(metrics.totalBookings?.current), fmt(metrics.totalBookings?.previous));
    push("Operations", "Jobs Completed", fmt(metrics.totalJobsCompleted?.current), fmt(metrics.totalJobsCompleted?.previous));
    push("Operations", "Avg Jobs/Day", fmt(metrics.avgJobsPerDay?.current, 1), fmt(metrics.avgJobsPerDay?.previous, 1));
    push("Operations", `Active ${crewPlural}`, fmt(metrics.activeCrew?.current), fmt(metrics.activeCrew?.previous));
    push("Operations", "Completion Rate %", fmt(metrics.completedVsScheduledPct?.current, 1), fmt(metrics.completedVsScheduledPct?.previous, 1));
    push("Operations", "Cancellation Rate %", fmt(metrics.cancellationRatePct?.current, 1), fmt(metrics.cancellationRatePct?.previous, 1));
    push("Operations", "No-Show Rate %", fmt(metrics.noShowRatePct?.current, 1), fmt(metrics.noShowRatePct?.previous, 1));
    rows.push([]);

    // Revenue
    push("Revenue", `Total Revenue (${currency})`, fmt(metrics.totalRevenue?.current, 2), fmt(metrics.totalRevenue?.previous, 2));
    push("Revenue", `Revenue/${bookingLabel} (${currency})`, fmt(metrics.revenuePerBooking?.current, 2), fmt(metrics.revenuePerBooking?.previous, 2));
    push("Revenue", `Avg Revenue/${crewSingular} (${currency})`, fmt(metrics.revenuePerCrewAvg?.current, 2), fmt(metrics.revenuePerCrewAvg?.previous, 2));
    push("Revenue", `Total ${customerLabel}s`, fmt(metrics.totalCustomers?.current), fmt(metrics.totalCustomers?.previous));
    push("Revenue", `Repeat ${customerLabel}s %`, fmt(metrics.repeatCustomersPct?.current, 1), fmt(metrics.repeatCustomersPct?.previous, 1));
    push("Revenue", `${bookingLabel}s/${customerLabel}`, fmt(metrics.bookingsPerCustomer?.current, 2), fmt(metrics.bookingsPerCustomer?.previous, 2));
    rows.push([]);

    // Leakage
    push("Leakage", `Lost Revenue - No Shows (${currency})`, fmt(metrics.lostRevenueNoShows?.current, 2), fmt(metrics.lostRevenueNoShows?.previous, 2));
    push("Leakage", `Lost Revenue - Cancellations (${currency})`, fmt(metrics.lostRevenueCancellations?.current, 2), fmt(metrics.lostRevenueCancellations?.previous, 2));
    push("Leakage", "No-Show Count", fmt(metrics.noShowCount?.current), fmt(metrics.noShowCount?.previous));
    push("Leakage", "Cancelled Count", fmt(metrics.cancelledCount?.current), fmt(metrics.cancelledCount?.previous));
    rows.push([]);

    // Bookings by Day
    if (metrics.bookingsByDay?.length) {
        rows.push([`${bookingLabel}s by Day`, "Date", "Current", "Previous"]);
        metrics.bookingsByDay.forEach((d) =>
            rows.push(["", d.day, d.current ?? 0, d.previous ?? 0])
        );
        rows.push([]);
    }

    // Crew Breakdown
    if (metrics.crewBreakdown?.length) {
        rows.push([`${crewPlural} Breakdown`, crewSingular, "Jobs (curr)", "Jobs (prev)", `Revenue curr (${currency})`, `Revenue prev (${currency})`]);
        metrics.crewBreakdown.forEach((c) =>
            rows.push([
                "",
                c.crew_name || `${crewSingular} ${c.crew_id}`,
                c.jobs ?? 0,
                c.prevJobs ?? 0,
                fmt(c.revenue, 2),
                fmt(c.prevRevenue, 2),
            ])
        );
    }

    // Escape cells that contain commas or quotes
    return rows
        .map((r) =>
            r.map((cell) => {
                const s = String(cell);
                return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
            }).join(sep)
        )
        .join("\n");
}

function downloadCSV(content, filename) {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.visibility = "hidden";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
}

// "No data" means nothing happened in the selected period — not just a missing/null metrics object
function hasNoData(metrics) {
    if (!metrics) return true;
    const noBookings = !metrics.totalBookings?.current && !metrics.totalBookings?.previous;
    const noRevenue = !metrics.totalRevenue?.current && !metrics.totalRevenue?.previous;
    const noDailyRows = !metrics.bookingsByDay?.length;
    return noBookings && noRevenue && noDailyRows;
}

// ── PDF via print ─────────────────────────────────────────────────────────────
function triggerPrint() {
    window.print();
}

// ── Component ─────────────────────────────────────────────────────────────────
const ExportButton = ({ metrics, currency, period, dateRange, isClinicService, isSportsService, crewSingular, crewPlural }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleCSV = () => {
        setOpen(false);
        if (hasNoData(metrics)) {
            toast.error("No data found for the selected period. Please choose a different date range.");
            return;
        }
        const label = dateRange?.start && dateRange?.end
            ? `${dateRange.start}_to_${dateRange.end}`
            : period;
        const content = buildCSV(metrics, currency, period, dateRange, isClinicService, isSportsService, crewSingular, crewPlural);
        downloadCSV(content, `analytics_${label}.csv`);
    };

    const handlePDF = () => {
        setOpen(false);
        triggerPrint();
    };

    return (
        <div className="export-wrapper" ref={ref}>
            <button
                className="export-main-btn"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
            >
                <span className="export-icon">↓</span>
                Export
                <span className="export-caret">{open ? "▲" : "▼"}</span>
            </button>

            {open && (
                <div className="export-dropdown" role="menu">
                    <button className="export-option" role="menuitem" onClick={handleCSV}>
                        <span className="export-option-icon">📄</span>
                        <div>
                            <div className="export-option-label">Export CSV</div>
                            <div className="export-option-sub">All metrics as spreadsheet</div>
                        </div>
                    </button>

                    <div className="export-divider" />

                    <button className="export-option" role="menuitem" onClick={handlePDF}>
                        <span className="export-option-icon">🖨️</span>
                        <div>
                            <div className="export-option-label">Export PDF</div>
                            <div className="export-option-sub">Print or save as PDF</div>
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default ExportButton;